import * as React from 'react';
import { ChevronRight, Folder, FolderOpen, Loader2 } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../primitives/collapsible';
import { cn } from '../lib/utils';

export interface TreeNode<T = unknown> {
  id: string;
  label: string;
  children?: TreeNode<T>[];
  data?: T;
  icon?: React.ComponentType<{ className?: string }>;
  loadChildren?: () => Promise<TreeNode<T>[]>;
}

export interface TreeViewProps<T = unknown> {
  nodes: TreeNode<T>[];
  selectedId?: string;
  onSelectNode?: (node: TreeNode<T>) => void;
  renderNode?: (node: TreeNode<T>) => React.ReactNode;
  className?: string;
  /** Initially expanded node IDs */
  defaultExpanded?: string[];
}

/**
 * Shared context driving roving-focus keyboard navigation and expand state
 * for the whole tree, per the WAI-ARIA Tree View design pattern.
 */
interface TreeContextValue {
  /** The node id that currently owns the single tab stop. */
  activeId: string | null;
  setActiveId: (id: string) => void;
  selectedId?: string;
  /** Set of node ids that are currently expanded. */
  expanded: Set<string>;
  setExpanded: (id: string, open: boolean) => void;
  /** Register a node so the tree can compute the visible ordering. */
  registerFocusable: (id: string, el: HTMLDivElement | null) => void;
  onKeyDown: (e: React.KeyboardEvent, id: string) => void;
}

const TreeContext = React.createContext<TreeContextValue | null>(null);

function useTreeContext(): TreeContextValue {
  const ctx = React.useContext(TreeContext);
  if (!ctx) throw new Error('TreeNodeView must be rendered inside TreeView');
  return ctx;
}

/**
 * Hierarchical tree view built on the `Collapsible` primitive.
 * Supports lazy loading of children and custom node rendering.
 *
 * Keyboard model (WAI-ARIA tree pattern):
 *   - ArrowDown / ArrowUp move between visible nodes (roving tabindex)
 *   - ArrowRight expands a collapsed parent, or moves to its first child
 *   - ArrowLeft collapses an expanded parent, or moves to the parent node
 *   - Home / End jump to the first / last visible node
 *   - Enter / Space select the focused node
 */
export function TreeView<T = unknown>({
  nodes,
  selectedId,
  onSelectNode,
  renderNode,
  className,
  defaultExpanded,
}: TreeViewProps<T>) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [activeId, setActiveIdState] = React.useState<string | null>(
    selectedId ?? nodes[0]?.id ?? null
  );
  const [expanded, setExpandedState] = React.useState<Set<string>>(
    () => new Set(defaultExpanded ?? [])
  );
  // id -> DOM element, for moving focus during keyboard navigation.
  const focusables = React.useRef(new Map<string, HTMLDivElement>());

  const setActiveId = React.useCallback((id: string) => {
    setActiveIdState(id);
  }, []);

  const setExpanded = React.useCallback((id: string, open: boolean) => {
    setExpandedState((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const registerFocusable = React.useCallback(
    (id: string, el: HTMLDivElement | null) => {
      if (el) focusables.current.set(id, el);
      else focusables.current.delete(id);
    },
    []
  );

  // Compute the document-order list of currently-visible treeitem ids by
  // reading the registered DOM nodes (they only mount when their ancestor is
  // expanded). Sorting by document position keeps navigation order correct.
  const visibleOrder = React.useCallback((): string[] => {
    const entries = Array.from(focusables.current.entries());
    entries.sort((a, b) => {
      const pos = a[1].compareDocumentPosition(b[1]);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    return entries.map(([id]) => id);
  }, []);

  const focusId = React.useCallback(
    (id: string | undefined) => {
      if (!id) return;
      setActiveIdState(id);
      // Focus after the active tabindex flips so the element is focusable.
      requestAnimationFrame(() => focusables.current.get(id)?.focus());
    },
    []
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent, id: string) => {
      const order = visibleOrder();
      const idx = order.indexOf(id);
      if (idx < 0) return;
      const el = focusables.current.get(id);
      const isExpandable = el?.dataset.expandable === 'true';
      const isOpen = el?.getAttribute('aria-expanded') === 'true';

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          focusId(order[Math.min(order.length - 1, idx + 1)]);
          break;
        case 'ArrowUp':
          e.preventDefault();
          focusId(order[Math.max(0, idx - 1)]);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (isExpandable && !isOpen) {
            setExpanded(id, true);
          } else if (isExpandable && isOpen) {
            focusId(order[Math.min(order.length - 1, idx + 1)]);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (isExpandable && isOpen) {
            setExpanded(id, false);
          } else {
            // Move to parent: nearest preceding node with a shallower level.
            const level = Number(el?.getAttribute('aria-level') ?? '1');
            for (let i = idx - 1; i >= 0; i--) {
              const candId = order[i];
              if (candId === undefined) continue;
              const cand = focusables.current.get(candId);
              if (Number(cand?.getAttribute('aria-level') ?? '1') < level) {
                focusId(candId);
                break;
              }
            }
          }
          break;
        case 'Home':
          e.preventDefault();
          focusId(order[0]);
          break;
        case 'End':
          e.preventDefault();
          focusId(order[order.length - 1]);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          el?.click();
          break;
        default:
          break;
      }
    },
    [visibleOrder, focusId, setExpanded]
  );

  const ctx: TreeContextValue = React.useMemo(
    () => ({
      activeId,
      setActiveId,
      selectedId,
      expanded,
      setExpanded,
      registerFocusable,
      onKeyDown: handleKeyDown,
    }),
    [activeId, setActiveId, selectedId, expanded, setExpanded, registerFocusable, handleKeyDown]
  );

  return (
    <TreeContext.Provider value={ctx}>
      <div
        ref={rootRef}
        className={cn(
          'rounded-md border bg-card p-2 text-sm text-card-foreground',
          className
        )}
        role="tree"
      >
        {nodes.map((node, i) => (
          <TreeNodeView
            key={node.id}
            node={node}
            level={1}
            posInSet={i + 1}
            setSize={nodes.length}
            onSelectNode={onSelectNode}
            renderNode={renderNode}
          />
        ))}
      </div>
    </TreeContext.Provider>
  );
}

interface TreeNodeViewProps<T> {
  node: TreeNode<T>;
  level: number;
  posInSet: number;
  setSize: number;
  onSelectNode?: (node: TreeNode<T>) => void;
  renderNode?: (node: TreeNode<T>) => React.ReactNode;
}

function TreeNodeView<T>({
  node,
  level,
  posInSet,
  setSize,
  onSelectNode,
  renderNode,
}: TreeNodeViewProps<T>) {
  const ctx = useTreeContext();
  const [loading, setLoading] = React.useState(false);
  const [children, setChildren] = React.useState(node.children);
  const itemRef = React.useRef<HTMLDivElement | null>(null);

  const open = ctx.expanded.has(node.id);

  const hasChildren = Boolean(
    (children && children.length > 0) || node.loadChildren
  );
  const isSelected = ctx.selectedId === node.id;
  const isActive = ctx.activeId === node.id;

  React.useEffect(() => {
    ctx.registerFocusable(node.id, itemRef.current);
    return () => ctx.registerFocusable(node.id, null);
  }, [ctx, node.id, open]);

  const handleOpenChange = async (next: boolean) => {
    if (
      next &&
      node.loadChildren &&
      (!children || children.length === 0) &&
      !loading
    ) {
      setLoading(true);
      try {
        const loaded = await node.loadChildren();
        setChildren(loaded);
      } finally {
        setLoading(false);
      }
    }
    ctx.setExpanded(node.id, next);
  };

  const Icon = node.icon ?? (hasChildren ? (open ? FolderOpen : Folder) : null);
  const paddingLeft = (level - 1) * 16 + 8;

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      <div
        ref={itemRef}
        className={cn(
          'group flex cursor-pointer select-none items-center gap-1 rounded-md px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring',
          isSelected && 'bg-accent text-accent-foreground'
        )}
        style={{ paddingLeft }}
        onClick={() => {
          ctx.setActiveId(node.id);
          onSelectNode?.(node);
        }}
        onKeyDown={(e) => ctx.onKeyDown(e, node.id)}
        onFocus={() => ctx.setActiveId(node.id)}
        role="treeitem"
        tabIndex={isActive ? 0 : -1}
        aria-expanded={hasChildren ? open : undefined}
        aria-selected={isSelected}
        aria-level={level}
        aria-posinset={posInSet}
        aria-setsize={setSize}
        data-expandable={hasChildren ? 'true' : 'false'}
      >
        {hasChildren ? (
          <CollapsibleTrigger
            asChild
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              tabIndex={-1}
              className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ChevronRight
                  className={cn(
                    'h-3.5 w-3.5 transition-transform',
                    open && 'rotate-90'
                  )}
                />
              )}
            </button>
          </CollapsibleTrigger>
        ) : (
          <span className="w-5" aria-hidden />
        )}
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="flex-1 truncate font-medium">
          {renderNode ? renderNode(node) : node.label}
        </span>
      </div>
      {hasChildren && (
        <CollapsibleContent>
          {children?.map((child, i) => (
            <TreeNodeView
              key={child.id}
              node={child}
              level={level + 1}
              posInSet={i + 1}
              setSize={children.length}
              onSelectNode={onSelectNode}
              renderNode={renderNode}
            />
          ))}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
